import React, { useEffect, useState } from "react";
import { Card, Spin, Carousel } from "antd";
import { Area, Line } from "@ant-design/charts";
import { ArrowUpOutlined, ArrowDownOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";

function StockMarketWidget() {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const carouselRef = React.useRef();

  useEffect(() => {
    fetchMarketData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchMarketData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  async function fetchMarketData() {
    try {
      // Mock data for Sri Lankan Stock Exchange
      // In production, you would fetch from CSE API or a financial data provider
      const mockData = {
        aspi: {
          name: "ASPI",
          fullName: "All Share Price Index",
          value: 11234.56,
          change: 127.34,
          changePercent: 1.15,
          chartData: generateChartData(11234.56, 30)
        },
        sp20: {
          name: "S&P SL20",
          fullName: "S&P Sri Lanka 20",
          value: 3456.78,
          change: -23.45,
          changePercent: -0.67,
          chartData: generateChartData(3456.78, 30)
        },
        turnover: {
          name: "Turnover",
          fullName: "Daily Turnover",
          value: 2345.67,
          unit: "LKR Mn",
          change: 345.23,
          changePercent: 17.25,
          chartData: generateTurnoverData(30)
        },
        topGainers: [
          { symbol: "JKH", price: 145.50, change: 8.50, changePercent: 6.20 },
          { symbol: "COMB", price: 89.75, change: 5.25, changePercent: 6.21 },
          { symbol: "HNB", price: 234.00, change: 12.00, changePercent: 5.41 }
        ],
        topLosers: [
          { symbol: "DIAL", price: 56.25, change: -4.75, changePercent: -7.79 },
          { symbol: "SAMP", price: 78.50, change: -3.50, changePercent: -4.27 },
          { symbol: "CARG", price: 123.75, change: -4.25, changePercent: -3.32 }
        ]
      };
      
      setMarketData(mockData);
    } catch (error) {
      console.error("Error fetching market data:", error);
    } finally {
      setLoading(false);
    }
  }

  function generateChartData(currentValue, days) {
    const data = [];
    let value = currentValue * 0.95;
    
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      value = value + (Math.random() - 0.48) * (currentValue * 0.02);
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseFloat(value.toFixed(2))
      });
    }
    
    return data;
  }

  function generateTurnoverData(days) {
    const data = [];
    
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      const value = 1500 + Math.random() * 2000;
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseFloat(value.toFixed(2))
      });
    }
    
    return data;
  }

  const IndexCard = ({ data, isPositive }) => {
    const config = {
      data: data.chartData,
      xField: 'date',
      yField: 'value',
      smooth: true,
      height: 200,
      autoFit: true,
      color: isPositive ? '#10b981' : '#ef4444',
      areaStyle: {
        fillOpacity: 0.3,
      },
      xAxis: {
        label: {
          style: {
            fontSize: 10,
          },
        },
      },
      yAxis: {
        label: null,
      },
      tooltip: {
        showMarkers: false,
      },
      animation: {
        appear: {
          animation: 'wave-in',
          duration: 800,
        },
      },
    };

    return (
      <div className="p-6">
        <div className="text-gray-500 text-sm mb-2">{data.fullName}</div>
        <div className="flex items-baseline gap-3 mb-4">
          <span className="text-3xl font-bold">{data.value.toLocaleString()}</span>
          {data.unit && <span className="text-gray-500 text-sm">{data.unit}</span>}
        </div>
        <div className={`flex items-center gap-2 mb-4 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          <span className="font-semibold">{Math.abs(data.change).toFixed(2)}</span>
          <span>({isPositive ? '+' : ''}{data.changePercent.toFixed(2)}%)</span>
        </div>
        <Area {...config} />
      </div>
    );
  };

  const StockListCard = ({ title, stocks, isGainers }) => (
    <div className="p-6">
      <div className="text-lg font-semibold mb-4">{title}</div>
      <div className="space-y-3">
        {stocks.map((stock, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <div>
              <div className="font-semibold text-gray-900">{stock.symbol}</div>
              <div className="text-sm text-gray-500">LKR {stock.price.toFixed(2)}</div>
            </div>
            <div className={`text-right ${isGainers ? 'text-green-600' : 'text-red-600'}`}>
              <div className="font-semibold flex items-center gap-1">
                {isGainers ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                {isGainers ? '+' : ''}{stock.changePercent.toFixed(2)}%
              </div>
              <div className="text-sm">
                {isGainers ? '+' : ''}{stock.change.toFixed(2)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="border rounded-md h-[500px] flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      className="border rounded-md"
      bodyStyle={{ padding: 0, height: '500px', display: 'flex', flexDirection: 'column' }}
      style={{ height: '500px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <span className="text-xl">📈</span>
          <span className="font-semibold">Colombo Stock Exchange</span>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => carouselRef.current?.prev()}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <LeftOutlined />
          </button>
          <button 
            onClick={() => carouselRef.current?.next()}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <RightOutlined />
          </button>
        </div>
      </div>

      {/* Carousel Content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Carousel ref={carouselRef} dots={{ className: 'custom-dots' }} autoplay={false}>
          {/* ASPI Index */}
          <div>
            <IndexCard 
              data={marketData.aspi} 
              isPositive={marketData.aspi.change > 0}
            />
          </div>

          {/* S&P SL20 Index */}
          <div>
            <IndexCard 
              data={marketData.sp20} 
              isPositive={marketData.sp20.change > 0}
            />
          </div>

          {/* Turnover */}
          <div>
            <IndexCard 
              data={marketData.turnover} 
              isPositive={marketData.turnover.change > 0}
            />
          </div>

          {/* Top Gainers */}
          <div>
            <StockListCard 
              title="Top Gainers" 
              stocks={marketData.topGainers}
              isGainers={true}
            />
          </div>

          {/* Top Losers */}
          <div>
            <StockListCard 
              title="Top Losers" 
              stocks={marketData.topLosers}
              isGainers={false}
            />
          </div>
        </Carousel>
      </div>

      {/* Footer */}
      <div className="p-3 text-center border-t bg-gray-50">
        <a
          href="https://www.cse.lk"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          View full market data on CSE →
        </a>
      </div>
    </Card>
  );
}

export default StockMarketWidget;
